import { useEffect, useState } from "react";
import { getVisitorFieldConfig, updateVisitorFieldConfig } from "./api";
import { useAdminResource } from "./use-admin-resource";
import { useToast } from "../../components/useToast";

export function VisitorFieldsPanel() {
  const { data: config, loading, error, refresh } = useAdminResource(() => getVisitorFieldConfig(), []);
  const { showSuccess, showError } = useToast();

  const [required, setRequired] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) setRequired(new Set(config.required_fields));
  }, [config]);

  function toggle(key: string) {
    setRequired((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function submit() {
    setSaving(true);
    try {
      // Preserve the catalog order so the saved list is stable.
      const ordered = (config?.configurable_fields ?? [])
        .map((f) => f.key)
        .filter((key) => required.has(key));
      await updateVisitorFieldConfig(ordered);
      showSuccess("Saved required fields");
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Visitor Entry Fields</h2>
        <span>{required.size} required</span>
      </header>
      <p className="management-note">
        Choose which visitor details are <strong>mandatory</strong> at check-in and on the visitor
        invite form. Full name and purpose of visit are always required. Photo, ID document and ID
        number are enforced in the browser; the rest are also enforced on the server.
      </p>

      {error ? <p className="error-banner">{error}</p> : null}

      <div className="action-card">
        {config?.always_required.map((field) => (
          <label key={field.key} className="checkbox-field" style={{ opacity: 0.6 }}>
            <input type="checkbox" checked disabled />
            {field.label} (always required)
          </label>
        ))}
        {(config?.configurable_fields ?? []).map((field) => (
          <label key={field.key} className="checkbox-field">
            <input
              type="checkbox"
              checked={required.has(field.key)}
              onChange={() => toggle(field.key)}
              disabled={loading}
            />
            {field.label}
          </label>
        ))}
      </div>

      <button className="refresh-btn action-submit" type="button" onClick={() => void submit()} disabled={saving || loading}>
        {saving ? "Saving..." : "Save Required Fields"}
      </button>
    </article>
  );
}
